// Shared "Eliminar cuenta" modal (owner-only, typed-confirmation hard delete
// — DELETE /admin/users/:id via deleteUser()). Used by both Users.tsx's row
// actions and UserDetail.tsx's Acciones card. The server double-checks
// owner-only + inactive-only; this modal only mirrors the confirm-typing UX.

import { useState } from 'react'

import { Button, FormField, Input, Modal, toast } from '../../../design-system/index.js'
import { apiErrorMessage } from '../../../lib/api-error.js'
import { deleteUser } from '../users-data.js'

export interface DeleteUserModalTarget {
  id: string
  email: string
}

export interface DeleteUserModalProps {
  target: DeleteUserModalTarget | null
  onClose: () => void
  onSuccess: () => void | Promise<void>
}

export function DeleteUserModal({ target, onClose, onSuccess }: DeleteUserModalProps): JSX.Element {
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  function close(): void {
    setConfirm('')
    onClose()
  }

  async function submit(): Promise<void> {
    if (!target) return
    setBusy(true)
    try {
      await deleteUser(target.id, confirm)
      toast.ok('Cuenta eliminada.')
      close()
      await onSuccess()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo eliminar la cuenta.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={target !== null}
      onClose={close}
      role="alertdialog"
      title="Eliminar cuenta"
      subtitle="Esta acción es permanente. Se borrará la cuenta y sus datos de acceso."
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={busy}
            disabled={!target || confirm !== target.email}
          >
            Eliminar definitivamente
          </Button>
        </>
      }
    >
      <FormField
        label={`Escribe "${target?.email ?? ''}" para confirmar`}
        htmlFor="delete-user-confirm"
      >
        <Input
          id="delete-user-confirm"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={target?.email ?? ''}
        />
      </FormField>
    </Modal>
  )
}
